///
/// EarthBrowser
///
/// Created by Matt Giger
/// Copyright (c) 2025 Geodata Labs
///

import { wgs } from "@/earthbrowser/utils/geographic"
import { spherical } from "@/earthbrowser/utils/spherical"
import { PanGestureRecognizer, type PanGestureEventData } from "@stovokor/gestures/PanGestureRecognizer"
import { clamp, trig } from "@stovokor/math"
import type { EarthCameraState, FulcrumOrbitAnchor, GroundDragAnchor } from "../EarthCameraState"
import { type EarthBrowserContext } from "@/earthbrowser/EarthBrowserContext"
import { GestureState, type TouchPoint } from "@stovokor/gestures/GestureRecognizer"
import { LowPassFilter } from "./LowPassFilter"

// Pixel sensitivity for the legacy pan, now only the fallback for drags that
// start in space (no ground under the pointer to lock to)
const cEarthRotationSensitivity = trig.pi / 15000000.0
const cCameraTiltSensitivity = trig.pi / 1000.0
const cCameraRotateSensitivity = trig.pi / 1000.0
const cMovementSpringDamping = 30
// Fling on release: the drag is projected ahead by the release velocity over
// a short window and the spring (softer, so the glide is longer) carries the
// camera there. The projection stays a little under the spring's time
// constant (projection * damping / 2 >= 1) so the glide decelerates without
// overshooting. A thumb flick expects the longer, lazier glide of a native
// scroll view; a mouse release reads better crisp
const cFlingByPointer = {
	mouse: { projectionSeconds: 0.2, damping: 10 },
	touch: { projectionSeconds: 0.32, damping: 6.5 },
}
const cFlingMinSpeedPx = 80
// No fling when the pointer paused this long before release
const cFlingMaxPauseMs = 100
// The projection's vertical FOV is fixed, so the visible earth spans
// proportionally fewer pixels in a smaller viewport (e.g. an embed). The
// per-pixel sensitivities above were tuned at this viewport height; drags
// are scaled by (reference / actual height) so a drag moves the globe the
// same visual distance regardless of window size.
const cReferenceViewportHeight = 1000
// Cutoff for the light input smoothing on the fulcrum orbit (higher = snappier)
const cOrbitFilterCutoffHz = 10

enum ShiftDragMode {
	None,
	Active
}

/**
 * EarthPanHandler - Pan gesture recognizer specialized for Earth navigation
 *
 * Extends PanGestureRecognizer to handle single-finger pan gestures that
 * rotate the camera around the Earth, allowing the user to explore
 * different geographic regions.
 *
 * The pan is ground-locked: the ground point under the pointer when the
 * drag began stays under the pointer (EarthCameraState.dragGroundTo), so a
 * drag moves the globe by exactly what the pointer covers at any latitude
 * or tilt, with the foreground naturally moving faster than the distance
 * when tilted. Targets still go through the critically damped spring, and
 * on release the drag is projected ahead by its velocity so the spring
 * carries a decelerating glide. Drags that start in space fall back to the
 * pixel-sensitivity model.
 *
 * When shift is held, horizontal and vertical drag are combined so the
 * camera bearing (horizontal) and tilt (vertical) update simultaneously
 * around the fulcrum point without having to release between motions.
 */
export class EarthPanHandler extends PanGestureRecognizer {
	private cameraState: EarthCameraState
	protected context: EarthBrowserContext
	// Pan anchor state
	private panAnchor = new spherical()
	private panAnchorTilt = 0
	private panAnchorBearing = 0
	private startX = 0
	private startY = 0
	// Ground-locked pan: undefined when the drag started in space
	private groundAnchor?: GroundDragAnchor
	private lastX = 0
	private lastY = 0
	private lastPointerMoveTime = 0
	private pointerType = "mouse"

	// Shift+drag state for combined tilt/rotate about the fulcrum
	private shiftDragMode = ShiftDragMode.None
	private orbitAnchor?: FulcrumOrbitAnchor
	// Captured once at gesture start so the bearing direction never flips
	// mid-drag when the pointer crosses the fulcrum line.
	private orbitDirection = 1
	// Light smoothing of the orbit deltas so direct manipulation isn't raw
	private rotateFilter = new LowPassFilter(cOrbitFilterCutoffHz)
	private tiltFilter = new LowPassFilter(cOrbitFilterCutoffHz)

	constructor(context: EarthBrowserContext, cameraState: EarthCameraState) {
		super({
			minimumMovement: 5,
			minimumTouches: 1,
			maximumTouches: 1,
		})
		this.cameraState = cameraState
		this.context = context
		this.priority = 10

		this.onGesture(this.panUpdate.bind(this))
	}

	/**
	 * A pointer landing on the globe catches a glide in flight (as a finger
	 * on a scrolling list does), before the pan threshold is reached - a
	 * still touch should hold the globe, not ride it
	 */
	override handleTouchStart(touches: TouchPoint[]): void {
		super.handleTouchStart(touches)
		if (this.touchCount === 1) {
			this.pointerType = touches[0]?.pointerType ?? "mouse"
			this.cameraState.catchGlide()
		}
	}

	private panUpdate(data: PanGestureEventData): void {
		switch (data.state) {
			case GestureState.Began:
				this.startPan(data)
				break
			case GestureState.Changed:
				this.updatePan(data)
				break
			case GestureState.Ended:
				this.endPan(data)
				break
		}
	}

	/**
	 * Override to capture pan anchor state when gesture begins
	 */
	private startPan(data: PanGestureEventData): void {
		if(!this.owner) return

		// Request focus with preserveCurrentState to adopt the current camera position
		// This allows seamless transition from any previous focus (e.g., satellite)
		this.context.focusManager.requestFocus(this.owner, {
			preserveCurrentState: true
		})

		// Sync the camera state from the current camera position
		// This ensures EarthCameraState knows where the camera actually is
		this.cameraState.syncFromCamera()

		// Capture anchor state before calling super
		const cam = this.cameraState.spherical
		this.panAnchor = new spherical(cam.radius, cam.theta, cam.phi)
		this.panAnchorTilt = this.cameraState.tilt
		this.panAnchorBearing = this.cameraState.bearing

		// Use the gesture's start position
		this.startX = data.x - data.translationX
		this.startY = data.y - data.translationY

		// Shift+drag: combined tilt + rotate around the fulcrum point
		this.groundAnchor = undefined
		if (data.keys.shiftKey) {
			this.shiftDragMode = ShiftDragMode.Active
			this.orbitAnchor = this.cameraState.captureFulcrumAnchor()
			this.orbitDirection = this.computeOrbitDirection(this.startY)
			this.rotateFilter.reset()
			this.tiltFilter.reset()
		} else {
			this.shiftDragMode = ShiftDragMode.None
			this.orbitAnchor = undefined
			const anchor = this.cameraState.captureGroundDragAnchor(this.startX, this.startY)
			if (anchor?.hit) this.groundAnchor = anchor
		}

		// Apply the initial translation
		this.applyPan(data)
	}

	/**
	 * Update the pan gesture
	 */
	private updatePan(data: PanGestureEventData): void {
		this.applyPan(data)
	}

	/**
	 * End the pan gesture
	 */
	private endPan(data: PanGestureEventData): void {
		if (this.shiftDragMode === ShiftDragMode.None) {
			this.fling(data)
		}
		this.shiftDragMode = ShiftDragMode.None
		this.groundAnchor = undefined
		this.cameraState.saveCameraState()
	}

	/**
	 * Carry a released drag on: project the pointer ahead by its velocity and
	 * send the spring (which still holds the drag's velocity) to the pose
	 * that puts the grabbed ground there. A critically damped spring to a
	 * projected destination is a smooth deceleration, so the glide keeps
	 * the same feel as every other motion instead of being a separate
	 * inertia model. Off-screen projections are fine: the ray still
	 * resolves, and the range cap bounds a sky-pointing one
	 */
	private fling(data: PanGestureEventData): void {
		const anchor = this.groundAnchor
		if (!anchor) return
		if (performance.now() - this.lastPointerMoveTime > cFlingMaxPauseMs) return
		const speed = Math.hypot(data.velocityX, data.velocityY)
		if (speed < cFlingMinSpeedPx) return

		const fling = this.pointerType === "touch" ? cFlingByPointer.touch : cFlingByPointer.mouse
		const target = this.cameraState.dragGroundTo(
			anchor,
			this.lastX + data.velocityX * fling.projectionSeconds,
			this.lastY + data.velocityY * fling.projectionSeconds
		)
		if (target) this.cameraState.animateGlide(target, fling.damping)
	}

	/**
	 * Apply pan translation to the camera
	 */
	private applyPan(data: PanGestureEventData): void {
		const viewportScale = this.viewportScale()
		const xDelta = (data.x - this.startX) * viewportScale
		const yDelta = (data.y - this.startY) * viewportScale

		// Handle shift+drag: tilt (vertical) and rotate (horizontal) together
		if (this.shiftDragMode === ShiftDragMode.Active) {
			this.applyTiltAndRotate(xDelta, yDelta)
			return
		}

		this.lastX = data.x
		this.lastY = data.y
		this.lastPointerMoveTime = performance.now()

		// Ground-locked pan
		if (this.groundAnchor) {
			const target = this.cameraState.dragGroundTo(this.groundAnchor, data.x, data.y)
			if (target) this.cameraState.animateTo(target, cMovementSpringDamping, "user")
			return
		}

		// Legacy pixel-sensitivity pan (drag started in space)
		const cam = this.cameraState.spherical
		const length = cam.radius
		const altitude = length - wgs.a

		// Calculate sensitivity based on altitude and tilt
		const baseSensitivity = altitude * cEarthRotationSensitivity
		const cosTilt = Math.cos(this.cameraState.tilt)
		const tiltFactor = cosTilt > 0.1 ? 1.0 / cosTilt : 10.0

		const horizontalSensitivity = baseSensitivity * clamp(1.0 + (tiltFactor - 1.0) * 0.3, 1.0, 3.0)
		const verticalSensitivity = baseSensitivity * clamp(tiltFactor, 1.0, 10.0)

		const panDeltaX = xDelta * horizontalSensitivity
		const panDeltaY = yDelta * verticalSensitivity

		// Apply bearing to pan direction
		const cosBearing = Math.cos(this.cameraState.bearing)
		const sinBearing = Math.sin(this.cameraState.bearing)
		const deltaTheta = -(panDeltaX * cosBearing - panDeltaY * sinBearing)
		const deltaPhi = -(panDeltaX * sinBearing + panDeltaY * cosBearing)

		let theta = this.panAnchor.theta + deltaTheta
		const phi = Math.max(0.001, Math.min(trig.pi - 0.001, this.panAnchor.phi + deltaPhi))

		const currentParams = this.cameraState.getCurrentParams()

		// Adjust theta for shortest path
		while (currentParams.theta - theta > trig.pi) theta += trig.two_pi
		while (currentParams.theta - theta < -trig.pi) theta -= trig.two_pi

		const newParams = this.cameraState.createParams(
			length,
			theta,
			phi,
			this.panAnchorTilt,
			this.panAnchorBearing
		)

		this.cameraState.animateTo(newParams, cMovementSpringDamping, "user")
	}

	/**
	 * Drag pixels are worth more rotation in a smaller viewport (fixed
	 * vertical FOV: the globe spans fewer pixels), so scale deltas by
	 * reference/actual height to keep the drag feel size-independent.
	 */
	private viewportScale(): number {
		const height = this.cameraState.camera?.projection.height ?? 0
		return height > 0 ? cReferenceViewportHeight / height : 1
	}

	/**
	 * Determine the horizontal-drag rotation direction from where the gesture
	 * started relative to the fulcrum on screen. Captured once at gesture start
	 * so it does not flip (and jerk) when the pointer later crosses the fulcrum.
	 */
	private computeOrbitDirection(mouseY: number): number {
		const camera = this.cameraState.camera
		if (!camera || !this.orbitAnchor) return 1
		const fulcrumScreen = camera.worldToScreen(this.orbitAnchor.fulcrum)
		return mouseY < fulcrumScreen.y ? 1 : -1
	}

	/**
	 * Apply combined tilt (vertical) and bearing rotation (horizontal) as a
	 * single rigid rotation about the fulcrum point. Delegates to the shared
	 * orbit model so the pivot stays fixed on screen during the drag, and applies
	 * the result immediately (no spring) so the camera tracks the pointer.
	 */
	private applyTiltAndRotate(xDelta: number, yDelta: number): void {
		const anchor = this.orbitAnchor
		if (!anchor) return

		const now = performance.now()
		const rotateDelta = this.rotateFilter.filter(this.orbitDirection * xDelta * cCameraRotateSensitivity, now)
		const tiltDelta = this.tiltFilter.filter(-yDelta * cCameraTiltSensitivity, now)

		const newParams = this.cameraState.orbitAboutFulcrum(anchor, rotateDelta, tiltDelta)
		if (!newParams) return

		this.cameraState.setParamsImmediate(newParams, "user")
	}

	override reset(): void {
		super.reset()
		this.panAnchor = new spherical()
		this.panAnchorTilt = 0
		this.panAnchorBearing = 0
		this.startX = 0
		this.startY = 0
		this.shiftDragMode = ShiftDragMode.None
		this.orbitAnchor = undefined
		this.orbitDirection = 1
		this.groundAnchor = undefined
		this.lastX = 0
		this.lastY = 0
		this.lastPointerMoveTime = 0
		this.rotateFilter.reset()
		this.tiltFilter.reset()
	}
}
